import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { type Entity, Relation } from "@/enums/entityProfiles";
import { useAuth } from "@/hooks/auth";
import { useUsersForFilters } from "@/hooks/useUsersForFilters";
import useEntityRelationsStore from "@/stores/entityRelations";

import type { IProfile } from "@/models/businesses";
import type { IResponseMeta } from "@/models/common";
import type {
  RelationByType,
  RelationshipEntry,
} from "@/models/entityProfiles";
import type { RelationOption } from "@/models/options";

export const useIndividualProfiles = () => {
  const { getters } = useStore();

  const individuals = computed<IProfile[]>(
    () => getters["profiles/individuals"]
  );

  const meta = computed<IResponseMeta>(() => getters["profiles/meta"]);

  return { individuals, meta };
};

export const useMutualColumns = (type: Entity) => {
  const { isLendflowUser } = useAuth();
  const { t } = useI18n();

  // Initialize user queries - TanStack Query will handle caching and deduplication
  useUsersForFilters();

  const columns = computed(() => [
    {
      label:
        (isLendflowUser ? `${t("COMMON.LENDFLOW")} ` : "") +
        t("DEALS.TABLE_HEADERS.UNDERWRITER"),
      key: isLendflowUser ? "underwriter" : "client_underwriter",
      component: "assign-user-field",
      props: {
        type: type,
        roleIdentifier: isLendflowUser
          ? "underwriter_id"
          : "client_underwriter_id",
      },
    },
    {
      label:
        (isLendflowUser ? `${t("COMMON.LENDFLOW")} ` : "") +
        t("DEALS.TABLE_HEADERS.FUNDING_ADVISOR"),
      key: isLendflowUser ? "funding_advisor" : "client_funding_advisor",
      component: "assign-user-field",
      props: {
        type: type,
        roleIdentifier: isLendflowUser
          ? "funding_advisor_id"
          : "client_funding_advisor_id",
      },
    },
    {
      label:
        (isLendflowUser ? `${t("COMMON.LENDFLOW")} ` : "") +
        t("DEALS.TABLE_HEADERS.ANALYST"),
      key: isLendflowUser ? "analyst" : "client_analyst",
      component: "assign-user-field",
      props: {
        type: type,
        roleIdentifier: isLendflowUser ? "analyst_id" : "client_analyst_id",
      },
    },
  ]);
  return columns;
};

export const useRelations = () => {
  const TYPE_RELATION_KEY = "type";
  const INVERSE_TYPE_RELATION_KEY = "inverse";
  const COMPANY_KEY = "company";
  const PARENT_KEY = "parent";

  const { getters } = useStore();
  const entityRelationsStore = useEntityRelationsStore();
  const { relations } = storeToRefs(entityRelationsStore);

  const relationships = computed(() => relations.value?.relationships || []);

  const relationsOptions = computed<RelationOption[]>(
    () => getters["options/businessRelationships"]
  );

  const getRelationByTypeElement = (
    relation: RelationOption,
    entities: RelationshipEntry[],
    key: typeof TYPE_RELATION_KEY | typeof INVERSE_TYPE_RELATION_KEY
  ): RelationByType => {
    let element: RelationByType = {
      type: relation[key].id,
      name_plural: relation[key].name_plural,
      children_options: relation[key].children || [],
      count: entities.length,
      relations: entities,
      has_toggle: false,
    };

    const childIds = [...(relation[key].children || [])].map(({ id }) => id);
    const childRelations = relationships.value.filter(({ type }) =>
      childIds.includes(type)
    );

    const children = Object.fromEntries(
      childIds
        .map((id) => [id, childRelations.filter((rel) => rel.type === id)])
        .filter(([, rels]) => rels?.length)
    );

    if (Object.keys(children)?.length) {
      element = {
        ...element,
        has_toggle: true,
        // relations = all relations, children = only child relations
        relations: [...(element.relations || []), ...childRelations],
        children,
      };
    }

    return element;
  };

  const relationsByType = computed(() =>
    relationsOptions.value.reduce(
      (acc: RelationByType[], relation: RelationOption) => {
        if (relation.type.id !== COMPANY_KEY) {
          const typeEntities = relationships.value.filter(
            (relationship) => relationship.type === relation.type.id
          );

          if (typeEntities?.length) {
            acc.push(
              getRelationByTypeElement(
                relation,
                typeEntities,
                TYPE_RELATION_KEY
              )
            );
          }
        }

        if (relation.inverse.id !== PARENT_KEY) {
          const inverseEntities = relationships.value.filter(
            (relationship) => relationship.type === relation.inverse.id
          );

          if (inverseEntities?.length) {
            acc.push(
              getRelationByTypeElement(
                relation,
                inverseEntities,
                INVERSE_TYPE_RELATION_KEY
              )
            );
          }
        }
        return acc;
      },
      []
    )
  );

  const relationsLength = computed(() => relationsByType.value.length || 1);

  const parent = computed(
    () =>
      relationships.value.find(
        (relationship) => relationship.type === Relation.parent
      ) || null
  );

  return {
    relationsByType,
    getRelationByTypeElement,
    mainEntity: relations,
    relationships,
    relationsOptions,
    relationsLength,
    parent,
  };
};
